/**
 * Campaign Table Component
 * 
 * Displays campaigns in a table format with:
 * - Campaign name and type badges
 * - Status badges
 * - Duration (start - end dates)
 * - Metrics (Active Promos, Total Usage, Revenue Impact, Revenue Uplift)
 * - Action buttons (Edit, Delete, Activate/Deactivate, Duplicate)
 * - Pagination
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table } from 'react-bootstrap';
import { Campaign, CampaignStatus } from '../../../../api/promos/types';
import { CampaignStatusBadge, CampaignTypeBadge } from '../../../../Common/BadgeStyles';
import '../Campaigns.css';

interface CampaignTableProps {
  campaigns: Campaign[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit?: (campaign: Campaign) => void;
  onView: (campaign: Campaign) => void;
  onDelete?: (campaign: Campaign) => void;
  onArchive?: (campaign: Campaign) => void;
  onToggleStatus?: (campaign: Campaign) => void;
  onDuplicate?: (campaign: Campaign) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = React.memo(({
  campaigns,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onDelete,
  onArchive,
  onToggleStatus,
  onDuplicate,
}) => {
  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const actionButtonRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Handle dropdown toggle
  const handleDropdown = (campaignId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (openDropdown === campaignId) {
      setOpenDropdown(null);
      setDropdownPosition(null);
      return;
    }
    
    // Get button reference - ensure it exists
    const buttonRef = actionButtonRefs.current[campaignId];
    if (!buttonRef) return;
    
    const rect = buttonRef.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 200;
    const dropdownWidth = 120;
    
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    let xPosition = rect.right - dropdownWidth;
    // If dropdown would go off left edge, align to left edge of button
    if (xPosition < 8) {
      xPosition = rect.left;
    }
    // If dropdown would go off right edge, adjust
    if (xPosition + dropdownWidth > viewportWidth - 8) {
      xPosition = viewportWidth - dropdownWidth - 8;
    }
    
    let yPosition: number;
    if (spaceBelow >= dropdownHeight) {
      // Enough space below - show below button
      yPosition = rect.bottom + 4;
    } else if (spaceAbove >= dropdownHeight) {
      // Not enough space below, but enough above - show above button
      yPosition = rect.top - dropdownHeight - 4;
    } else {
      // Not enough space either way - show below but clamp to viewport
      const maxY = Math.max(8, viewportHeight - dropdownHeight - 8);
      yPosition = Math.min(rect.bottom + 4, maxY);
    }
    
    const newPosition = { x: xPosition, y: yPosition };
    setOpenDropdown(campaignId);
    setDropdownPosition(newPosition);
  };
  
  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown) {
        const dropdownRef = dropdownRefs.current[openDropdown];
        const buttonRef = actionButtonRefs.current[openDropdown];
        
        if (
          dropdownRef &&
          !dropdownRef.contains(event.target as Node) &&
          buttonRef &&
          !buttonRef.contains(event.target as Node)
        ) {
          setOpenDropdown(null);
          setDropdownPosition(null);
        }
      }
    };
    
    const handleScroll = () => {
      if (openDropdown) {
        const buttonRef = actionButtonRefs.current[openDropdown];
        if (buttonRef) {
          requestAnimationFrame(() => {
            const rect = buttonRef.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const dropdownHeight = 200;
            const dropdownWidth = 120;
            
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            let xPosition = rect.right - dropdownWidth;
            if (xPosition < 8) {
              xPosition = rect.left;
            }
            if (xPosition + dropdownWidth > viewportWidth - 8) {
              xPosition = viewportWidth - dropdownWidth - 8;
            }
            
            let yPosition: number;
            if (spaceBelow >= dropdownHeight) {
              yPosition = rect.bottom + 4;
            } else if (spaceAbove >= dropdownHeight) {
              yPosition = rect.top - dropdownHeight - 4;
            } else {
              const maxY = Math.max(8, viewportHeight - dropdownHeight - 8);
              yPosition = Math.min(rect.bottom + 4, maxY);
            }
            
            setDropdownPosition({ x: xPosition, y: yPosition });
          });
        } else {
          // Button no longer visible - close dropdown
          setOpenDropdown(null);
          setDropdownPosition(null);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scrolls
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [openDropdown]);
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const totalPages = Math.ceil(totalCount / pageSize);
  
  return (
    <div className="campaign-table-wrapper">
        <Table responsive className="campaign-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Campaign Type</th>
              <th>Status</th>
              <th>Duration</th>
              <th className="text-center">Active Promos</th>
              <th className="text-center">Total Usage</th>
              <th className="text-center">Revenue Impact</th>
              <th className="text-center">Revenue Uplift</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr key={campaign.id}>
                {/* Campaign Name */}
                <td>
                  <div className="campaign-name">
                    <button
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => onView(campaign)}
                      style={{ color: '#006EFF', fontWeight: 400 }}
                    >
                      {campaign.name}
                    </button>
                  </div>
                </td>
                
                {/* Campaign Type */}
                <td>
                  <CampaignTypeBadge type={campaign.campaignType} />
                </td>
                
                {/* Status */}
                <td>
                  <CampaignStatusBadge status={campaign.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'SCHEDULED' | 'INACTIVE'} />
                </td>
                
                {/* Duration */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '13px', color: '#495057', whiteSpace: 'nowrap' }}>
                    {formatDate(campaign.startAt)} → {formatDate(campaign.endAt)}
                  </div>
                </td>
                
                {/* Active Promos */}
                <td className="text-center">
                  <span style={{ fontWeight: 400 }}>
                    {campaign.activePromos || 0}
                  </span>
                </td>
                
                {/* Total Usage */}
                <td className="text-center">
                  <span style={{ fontWeight: 400 }}>
                    {campaign.totalUsage ? campaign.totalUsage.toLocaleString() : '0'}
                  </span>
                </td>
                
                {/* Revenue Impact */}
                <td className="text-center">
                  <span style={{ color: '#FB3A15', fontWeight: 400 }}>
                    {campaign.revenueImpact ? formatCurrency(campaign.revenueImpact) : '₹0'}
                  </span>
                </td>
                
                {/* Revenue Uplift */}
                <td className="text-center">
                  <span style={{ color: '#28a745', fontWeight: 400 }}>
                    {campaign.revenueUplift || '+0%'}
                  </span>
                </td>
                
                {/* Actions - 3 dots dropdown (only when mutating callbacks are provided) */}
                <td style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  {(onEdit || onToggleStatus || onDelete || onArchive || onDuplicate) && (
                    <>
                      <span
                        className="actions-icon"
                        title="Actions"
                        onClick={(e) => handleDropdown(campaign.id, e)}
                        ref={(el) => {
                          if (el) {
                            actionButtonRefs.current[campaign.id] = el;
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: '#6B7280',
                          display: 'inline-block',
                          lineHeight: '1',
                          userSelect: 'none',
                        }}
                      >
                        &#8942;
                      </span>
                      {openDropdown === campaign.id && dropdownPosition && createPortal(
                        <div
                          className="dropdown-menu-actions"
                          ref={(el) => {
                            if (el) {
                              dropdownRefs.current[campaign.id] = el;
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'fixed',
                            top: `${dropdownPosition.y}px`,
                            left: `${dropdownPosition.x}px`,
                            zIndex: 9999,
                          }}
                        >
                          <div
                            className="dropdown-item"
                            onClick={() => {
                              onView(campaign);
                              setOpenDropdown(null);
                              setDropdownPosition(null);
                            }}
                          >
                            View
                          </div>
                          {onEdit && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onEdit(campaign);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Edit
                            </div>
                          )}
                          {onToggleStatus && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onToggleStatus(campaign);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              {campaign.status === CampaignStatus.ACTIVE ? 'Pause' : 'Activate'}
                            </div>
                          )}
                          {(onArchive || onDelete) && (
                            <div
                              className="dropdown-item archive"
                              onClick={() => {
                                if (onArchive) {
                                  onArchive(campaign);
                                } else if (onDelete) {
                                  onDelete(campaign);
                                }
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Archive
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
    </div>
  );
});

CampaignTable.displayName = 'CampaignTable';

export default CampaignTable;